import { isTimeOfDayBefore, isValidTimeOfDay } from 'shared/entities/date';
import { TranslationString } from 'shared/entities/localization';
import {
  validateTextFieldMaxLength,
  validatorTransFunctions,
  ValidatorResult
} from 'shared/entities/validator';

import {
  BUSINESS_HOURS_AUTO_REPLY_MAX_LENGTH,
  BUSINESS_HOURS_WEEKDAYS,
  IBusinessHoursDay,
  IBusinessHoursSettings
} from './client';
import { BusinessHoursWeekday } from './server';

export const DEFAULT_WORKING_DAY_START = '09:00';
export const DEFAULT_WORKING_DAY_END = '18:00';

export enum BusinessHoursValidationError {
  invalidTime = 'invalidTime',
  invalidTimeRange = 'invalidTimeRange'
}

export const createDefaultBusinessHoursDay = (
  weekday: BusinessHoursWeekday
): IBusinessHoursDay => ({
  weekday,
  dayOff: true,
  start: null,
  end: null
});

export const createDefaultBusinessHoursSettings =
  (): IBusinessHoursSettings => {
    const workingWeekdays = new Set<BusinessHoursWeekday>([
      BusinessHoursWeekday.monday,
      BusinessHoursWeekday.tuesday,
      BusinessHoursWeekday.wednesday,
      BusinessHoursWeekday.thursday,
      BusinessHoursWeekday.friday
    ]);

    return {
      enabled: false,
      schedule: BUSINESS_HOURS_WEEKDAYS.map((weekday) =>
        workingWeekdays.has(weekday)
          ? {
              weekday,
              dayOff: false,
              start: DEFAULT_WORKING_DAY_START,
              end: DEFAULT_WORKING_DAY_END
            }
          : createDefaultBusinessHoursDay(weekday)
      ),
      autoReply: {
        enabled: false,
        text: ''
      }
    };
  };

export const mapBusinessHoursValidationErrorToMessage = (
  code: BusinessHoursValidationError
): TranslationString => {
  switch (code) {
    case BusinessHoursValidationError.invalidTime:
      return (t) =>
        t('chats.businessHours.validation.invalidTime', {
          ns: 'projectSettingsPage'
        });
    case BusinessHoursValidationError.invalidTimeRange:
      return (t) =>
        t('chats.businessHours.validation.invalidTimeRange', {
          ns: 'projectSettingsPage'
        });
  }
};

export const validateBusinessHoursTime = (
  value: string | null,
  dayOff: boolean
): ValidatorResult => {
  if (dayOff || isValidTimeOfDay(value)) {
    return null;
  }

  return value
    ? mapBusinessHoursValidationErrorToMessage(
        BusinessHoursValidationError.invalidTime
      )
    : validatorTransFunctions.requiredField;
};

export const validateBusinessHoursTimeRange = (
  start: string | null,
  end: string | null,
  dayOff: boolean
): ValidatorResult => {
  if (
    dayOff ||
    !isValidTimeOfDay(start) ||
    !isValidTimeOfDay(end) ||
    isTimeOfDayBefore(start, end)
  ) {
    return null;
  }

  return mapBusinessHoursValidationErrorToMessage(
    BusinessHoursValidationError.invalidTimeRange
  );
};

export const validateBusinessHoursAutoReply = (
  value: string,
  enabled: boolean
): ValidatorResult => {
  if (enabled && !value.trim()) {
    return validatorTransFunctions.requiredField;
  }

  return validateTextFieldMaxLength(
    value,
    BUSINESS_HOURS_AUTO_REPLY_MAX_LENGTH
  );
};

export const isBusinessHoursWorkingDayValid = (
  dayOff: boolean,
  start: string | null,
  end: string | null
): boolean => dayOff || isTimeOfDayBefore(start, end);
